import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Resolve Entity Watchers Step
 *
 * Generic, reusable step that resolves watchers for any watchable entity.
 * Queries the CoreWatch table for watchers of the given entity type + ID,
 * filtered by watch kind, and returns their email addresses.
 *
 * This step is entity-agnostic — it works for any watchable entity type
 * (staged_purchase_order, sales_order, customer, etc.).
 */
export const resolveEntityWatchersStep = defineStepConfig({
  type: "resolve_entity_watchers",
  name: "Resolve Entity Watchers",
  description:
    "Resolves watcher email addresses for a given entity type and ID. Filters by watch kind and respects user notification preferences.",
  category: "Data Access",
  deprecated: {
    reason:
      "Legacy order-notification flow. OMS v3 routes notifications through the Approval step + NotificationService (with out-of-office substitution); this standalone watcher resolver is no longer used.",
  },

  inputSchema: z.object({
    watchableId: z
      .uuid()
      .nullable()
      .describe(
        "The ID of the entity to find watchers for. If null, returns empty results.",
      ),
    watchRolesOverride: z
      .union([z.string(), z.array(z.string())])
      .nullable()
      .optional()
      .describe(
        "Dynamic watch role(s) to filter by, overriding config.watchRoles. " +
          "Accepts a single string or array of strings. " +
          "Use this for multi-level approval workflows where the watch role " +
          "is determined at runtime. If null/undefined, uses config.watchRoles.",
      ),
    excludeUserIds: z
      .array(z.uuid())
      .nullable()
      .optional()
      .describe(
        "User IDs to exclude from the resolved recipient list. " +
          "Use this to suppress notifications for a user who just triggered " +
          "the workflow (e.g., the approver who cleared the last discount " +
          "and is already on the page). Null/undefined means no exclusion.",
      ),
  }),

  outputSchema: z.object({
    recipientEmails: z
      .array(z.string())
      .describe("Email addresses of resolved watchers"),
    watcherCount: z
      .number()
      .describe("Total number of watchers found (before preference filtering)"),
  }),

  configSchema: z.object({
    watchableEntityType: z
      .string()
      .describe(
        "The WatchableEntityType value (e.g. 'staged_purchase_order'). Set at template design time.",
      ),
    watchKind: z
      .enum(["watching", "assigned"])
      .default("assigned")
      .describe(
        "Which watch kind to filter by. Defaults to 'assigned' (explicitly assigned users).",
      ),
    watchRoles: z
      .array(z.string())
      .optional()
      .describe(
        "Optional array of watch roles to filter by. If set, only watchers with one of these roles will be returned. Roles are org-specific.",
      ),
    notificationPreferenceKey: z
      .string()
      .optional()
      .describe(
        "Optional user notification preference key to check. If set, users who have this preference set to false will be excluded.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 seconds",
    retry: {
      maximumAttempts: 3,
    },
  },
});
